/* eslint-disable max-lines */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath } from 'react-router-dom';

//ui component
import { IconButton, Typography, CircularProgress } from '@material-ui/core';
import { ImageShow, ImageEditor, CancelButton, SubmitButton, CalendarInput } from '../../UI';
import { DateObject } from 'react-multi-date-picker';

//utils
import { useStyle } from './styles.js';
import moment from 'moment-jalaali';
import { useStorage } from '../../../hooks';
import * as labActions from '../../../redux/Laboratory/LaboratoryAction';
import dataUrlToBlob from '../../../utils/dataUrlToBlob';
import isEqual from 'lodash/isEqual';
import MessagesContext from '../../../contexts/messagesContext';
import { getFullDate, getTime } from '../../../utils/dateTimeUtil';

//assets
import Add from '../../../assets/images/add.png';
import ArrowRight from '../../../assets/images/blue_arrow.png';
import useCheckMobileScreen from '../../../hooks/useWidth';
import AddFab from '../AddFab';
import WithPatientFileHeader from '../../PatientFileHeader/WithPatientFileHeader';
import arrowIcon from '../../../assets/images/Icon/arrowMobile.svg';
// import NavBar from '../../NavBar';

function ResultImages({ match, history }) {
    /**
     * @type React.MutableRefObject<DatePicker>
     */
    const isMobile = useCheckMobileScreen();
    const { messages } = useContext(MessagesContext);
    const datePickerRef = useRef();
    const dispatch = useDispatch();
    const [info, setInfo] = useState({
        issued_datetime: moment(),
        issued_datetime_string: '',
        images: [],
        edited: [],
        newImages: [],
        imageResultId: match?.params?.image_result_id,
        dateInitialized: false
    });

    const isDataChanged = useMemo(
        () =>
            info.newImages.length ||
            info.issued_datetime?.diff(moment(info.issued_datetime_string)),
        [info.issued_datetime, info.newImages, info.issued_datetime_string]
    );

    const classes = useStyle({ isDataChanged, isMobile });

    const [deleteLoading, setDeleteLoading] = useState([]);

    const listView = useRef();

    // const date = useMemo(() => info.issued_datetime?.format('jYY/jMM/jDD'), [info.issued_datetime]);
    // const time = useMemo(() => info.issued_datetime?.format('hh:mm'), [info.issued_datetime]);

    const [modal, setModal] = useState({
        visible: false,
        image: null
    });

    const { laboratoryImages, loading } = useSelector((store) => ({
        laboratoryImages: store.laboratory.laboratoryTestImagesResults,
        loading: store.laboratory.loading
    }));
    const { image_result_id, id: patient_id } = match.params;
    const isNewResultImage = image_result_id === 'new';
    const _maxDate = useMemo(() => moment(), []);

    const { selectFile } = useStorage({
        limit: 1,
        accept: 'Image/*',
        onRead: (dataUrls) => {
            setInfo((state) => ({
                ...state,
                images: [
                    ...state.images,
                    {
                        url: dataUrls[0],
                        id: `new${state.images.filter((v) => `${v.id}`.includes('new')).length}`
                    }
                ]
            }));
            const timeout = setTimeout(() => {
                listView.current.parentElement.scrollTop =
                    listView.current.parentElement.scrollHeight;
                clearTimeout(timeout);
            }, 100);
        },
        onChange: (files) => {
            setInfo((state) => ({
                ...state,
                newImages: [
                    { file: files[0], id: `new${state.newImages.length}` },
                    ...state.newImages
                ]
            }));
        }
    });

    useEffect(() => {
        if (loading) return;
        const indexOfItem = laboratoryImages.map((v) => `${v.id}`).indexOf(image_result_id);
        if (indexOfItem >= 0) {
            const imageResult = laboratoryImages[indexOfItem];
            if (!isEqual(imageResult.images, info.images))
                setInfo((state) => ({
                    ...state,
                    ...imageResult,
                    issued_datetime_string: imageResult.issued_datetime,
                    issued_datetime: moment(imageResult.issued_datetime)
                }));
            return;
        } else if (!isNewResultImage) {
            history.goBack();
        } else if (isNewResultImage && info.dateInitialized) {
            datePickerRef.current.setOpen(true);
            setInfo((state) => ({
                ...state,
                dateInitialized: true
            }));
        }
        // eslint-disable-next-line
    }, [laboratoryImages, image_result_id, loading]);

    // const handleDateChange = useCallback(
    //     (date) => {
    //         const dateValue = new Date(date);
    //         // if (info.issued_datetime?.diff(dateValue) !== 0) {
    //         //     setInfo((info) => ({ ...info, issued_datetime: dateValue }));
    //         // }
    //     },
    //     [info.issued_datetime]
    // );

    const handleDateChange = (date) => {
        const dateValue = new Date(date);
        const convertToMoment = moment(dateValue);
        if (info.issued_datetime?.diff(convertToMoment) !== 0) {
            setInfo((info) => ({ ...info, issued_datetime: convertToMoment }));
        }
    };

    // const showDatePicker = () => {
    //     datePickerRef.current.setOpen(true);
    //     datePickerRef.current.forceUpdate();
    // };

    const handleModal = (image) => () => {
        setModal({
            visible: true,
            image
        });
    };
    const handleModalDismiss = () => {
        setModal({
            visible: false
        });
    };

    const handleDeleteImage = (id) => () => {
        handleModalDismiss();
        if (`${id}`.includes('new')) {
            setInfo((state) => ({
                ...state,
                images: state.images.filter((v) => v.id !== id),
                newImages: state.newImages.filter((v) => v.id !== id)
            }));
        } else {
            setDeleteLoading((state) => [...state, id]);
            dispatch(
                labActions.deleteImageFromLaboratoryTestImageResult(image_result_id, id, () => {
                    setDeleteLoading((state) => state.filter((image) => image !== id));
                })
            );
        }
    };

    const handleEditImage = (image) => (dataUrl) => {
        const imageFile = dataUrlToBlob(dataUrl, `synapps${image.id}`);
        const newImages = info.newImages.map((v) =>
            v.id === image.id ? { id: image.id, file: imageFile } : v
        );
        const images = info.images.map((v) =>
            v.id === image.id ? { id: image.id, url: dataUrl } : v
        );
        setInfo((state) => ({
            ...state,
            images,
            newImages
        }));
    };

    const uploadImages = (images, imageResultId) =>
        new Promise((res, rej) => {
            const status = new Proxy(
                {
                    failed: [],
                    inProgress: [],
                    succeeded: [],
                    imageResultId
                },
                {
                    set: (target, p, value) => {
                        if (p === 'succeeded' || p === 'failed') {
                            const lastIndex = value.length - 1;
                            const newValue = value[lastIndex];
                            target['inProgress'] = target['inProgress'].filter(
                                (v) => v?.id !== newValue?.id
                            );
                            target[p] = value;
                            const succeeded = target.succeeded;
                            const failed = target.failed;
                            if (succeeded.length + failed.length === images.length) {
                                if (!failed.length) {
                                    res({
                                        ...target,
                                        failed,
                                        succeeded
                                    });
                                } else {
                                    rej(target);
                                }
                            }
                        } else {
                            target[p] = value;
                        }

                        return true;
                    }
                }
            );

            const Request = (images = [], index = 0) => {
                const isNotNewResult = status.imageResultId !== 'new';
                const image = images[index];
                status.inProgress = [...status.inProgress, image];
                if (status.failed.length) {
                    status.failed = [...status.failed, image];
                    if (index < images.length - 1) {
                        Request(images, index + 1);
                    }
                    return;
                }
                dispatch(
                    (isNotNewResult
                        ? labActions.addImageToLaboratoryTestImageResult
                        : labActions.createLaboratoryTestImageResult)({
                        imageResultId: status.imageResultId,
                        image: image.file,
                        patient_id: parseInt(patient_id), // for createLaboratoryTestImageResult
                        issued_datetime: info.issued_datetime?.format(), // for createLaboratoryTestImageResult
                        onDone: (data) => {
                            if (status.imageResultId === 'new') {
                                status.imageResultId = data.id;
                            }
                            status.succeeded = [...status.succeeded, image];

                            if (index < images.length - 1) {
                                Request(images, index + 1);
                            }
                        },
                        onError: () => {
                            status.failed = [...status.failed, image];

                            if (index < images.length - 1) {
                                Request(images, index + 1);
                            }
                        }
                    })
                );
            };

            Request(images);
            // images.forEach((image) => {});
        });

    const handleCancel = () => {
        setInfo((state) => ({
            ...state,
            newImages: [],
            images: state.images.filter((v) => !`${v.id}`.includes('new')),
            issued_datetime: moment()
        }));
    };

    const handleSave = async () => {
        try {
            dispatch(labActions.setFetchingState({ loading: true }));
            const data = await uploadImages(info.newImages, info.imageResultId);
            setInfo((state) => ({ ...state, newImages: [] }));
            history.replace({
                pathname: generatePath(match.path, {
                    ...match?.params,
                    image_result_id: data.imageResultId
                })
            });
            dispatch(labActions.getLaboratoryTests(patient_id));
            dispatch({
                type: 'ADD_MESSAGE',
                payload: {
                    text: messages.alerts.success,
                    status: 200
                }
            });
        } catch (data) {
            dispatch(labActions.setFetchingState({ loading: false }));

            setInfo((state) => ({
                ...state,
                newImages: data?.failed,
                imageResultId: data?.imageResultId
            }));
            dispatch({
                type: 'ADD_MESSAGE',
                payload: {
                    text: messages.alerts.errorLabImageUpload,
                    status: 400
                }
            });
            dispatch(labActions.setFetchingState({ loading: false }));
        }
    };

    const handleNavigateToNextResults = () => {
        const ids = laboratoryImages.map((v) => `${v.id}`);
        const thisResult = ids.indexOf(`${image_result_id}`);
        const nextResult = ids[thisResult + 1];
        if (nextResult) {
            history.replace({
                pathname: generatePath(match.path, {
                    ...match?.params,
                    image_result_id: nextResult
                })
            });
        }
    };
    const handleNavigateToPrevResults = () => {
        const ids = laboratoryImages.map((v) => `${v.id}`);
        const thisResult = ids.indexOf(image_result_id);
        const prevResult = ids[thisResult - 1];
        if (prevResult) {
            history.replace({
                pathname: generatePath(match.path, {
                    ...match?.params,
                    image_result_id: prevResult
                })
            });
        }
    };

    return (
        <>
            <div className={classes.container} ref={listView}>
                <div className={classes.header}>
                    {!isMobile && (
                        <div className={classes.backWrapper} onClick={history?.goBack}>
                            <img src={arrowIcon} />
                            <span>{messages.back}</span>
                        </div>
                        // <IconButton onClick={history.goBack}>
                        //     <KeyboardBackSpace />
                        // </IconButton>
                    )}
                    <div className={classes.headerContent}>
                        <IconButton onClick={handleNavigateToNextResults} data-testid="nextIcon">
                            <img src={ArrowRight} alt="next result" />
                        </IconButton>
                        <div className={classes.dateContainer}>
                            {!isMobile && (
                                <Typography className={classes.dateTitle}>
                                    {messages.date}:
                                </Typography>
                            )}
                            <span className={classes.dateValue}>
                                {getFullDate(info.issued_datetime)}
                            </span>
                            <div className={classes.datePickerContainer}>
                                <CalendarInput
                                    customClasses={classes.calender}
                                    value={info.issued_datetime}
                                    onChange={(date) => handleDateChange(new DateObject(date))}
                                    max={_maxDate}
                                    hasTimePicker
                                    usePortal
                                />
                            </div>
                            {!isMobile && (
                                <Typography className={classes.dateTitle}>
                                    {messages.hour}:
                                </Typography>
                            )}
                            <span className={classes.dateValue}>
                                {getTime(info.issued_datetime)}
                            </span>
                        </div>
                        <IconButton onClick={handleNavigateToPrevResults} data-testid="prevIcon">
                            <img className={classes.arrowLeft} src={ArrowRight} alt="prev result" />
                        </IconButton>
                    </div>
                </div>

                <div className={classes.imageList}>
                    {info.images.length ? (
                        info.images.map((image) => (
                            <div className={classes.imageContainer} key={`${image.id}`}>
                                <ImageEditor
                                    onClick={handleModal(image)}
                                    src={image.url}
                                    className={classes.image}
                                    onImageEdited={handleEditImage(image)}
                                    disableEditing={!`${image.id}`.includes('new')}
                                    handleDelete={handleDeleteImage(image.id)}
                                    deleteLoading={deleteLoading.includes(image.id)}
                                />
                            </div>
                        ))
                    ) : (
                        <div
                            className={classes.onEmptyComponent}
                            onClick={selectFile}
                            data-testid="uploadFile"
                        >
                            <Typography>{messages.addLabImageHint}</Typography>
                            <IconButton>
                                <img src={Add} alt="add" />
                            </IconButton>
                        </div>
                    )}
                </div>
                <div className={classes.footer}>
                    <div className={classes.confirmButtonsContainer}>
                        <CancelButton onClick={handleCancel} cancelText={messages.cancel} />

                        <SubmitButton handleClick={handleSave} disableSubmit>
                            {`${messages.saveLab}  ${info.newImages.length || ''}`}
                            {info.newImages.length && loading ? (
                                <CircularProgress size="1.5em" color="inherit"></CircularProgress>
                            ) : null}
                        </SubmitButton>
                    </div>

                    {isMobile ? (
                        <AddFab onClick={selectFile} bottom={40} right={24} />
                    ) : (
                        <SubmitButton
                            handleClick={selectFile}
                            submitText={messages.addImage}
                            disableSubmit
                        />
                    )}
                </div>

                <ImageShow
                    src={modal.image?.url}
                    visible={modal.visible}
                    handleDelete={handleDeleteImage(modal.image?.id)}
                    handleDismiss={handleModalDismiss}
                />
            </div>
        </>
    );
}

ResultImages.propTypes = {
    match: PropTypes.object,
    history: PropTypes.object
};

export default WithPatientFileHeader(ResultImages, ['sidebar', 'laboratory'], true);
